import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AudioWaveform,
  ArrowLeft,
  Check,
  Zap,
  Music2,
  Crown,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const PricingPage = () => {
  const handleSelectPlan = (plan) => {
    toast.info(
      plan === "Free"
        ? "Free ya esta disponible al crear tu cuenta"
        : `${plan} esta en beta: el checkout/billing aun no esta activo`
    );
  };

  const plans = [
    {
      name: "Free",
      price: "0",
      period: "Siempre gratis",
      description: "MVP actual para practicar y explorar",
      features: [
        "Analisis musical basico",
        "Deteccion de tono y BPM",
        "Acordes y estructura",
        "Loop basico por seccion",
        "Asistente IA con limite diario"
      ],
      limitations: [
        "No guarda presets Pro de Stem Mixer",
        "Sin export PDF completo con acordes",
        "Sin billing/upgrade automatico aun"
      ],
      cta: "Empezar gratis",
      highlighted: false,
      icon: <Music2 className="w-6 h-6" />
    },
    {
      name: "Pro",
      price: "Beta",
      period: "sin checkout activo",
      description: "Capacidades Pro para cuentas marcadas como Pro",
      badge: "MVP Pro",
      features: [
        "Asistente IA sin limite diario Free",
        "Guardar presets de Stem Mixer",
        "Practice con velocidad variable",
        "Mute de stems en practica",
        "Export PDF completo de letra + acordes",
        "Flujos de ensayo avanzados en beta"
      ],
      limitations: [
        "Pagos reales proximamente",
        "No incluye export de audio renderizado"
      ],
      cta: "Solicitar acceso Pro",
      highlighted: true,
      icon: <Zap className="w-6 h-6" />
    },
    {
      name: "Studio",
      price: "Soon",
      period: "roadmap",
      description: "Fase futura para equipos y flujos profesionales",
      badge: "Coming soon",
      features: [
        "Billing y gestion de equipos",
        "Export/render de audio",
        "Pan profesional y sync avanzado",
        "Batch processing",
        "Integraciones profesionales"
      ],
      limitations: ["No implementado en el MVP actual"],
      cta: "Coming soon",
      highlighted: false,
      icon: <Crown className="w-6 h-6" />
    }
  ];

  const comparisonRows = [
    { feature: "Analisis de tonalidad/BPM", free: true, pro: true, studio: "Roadmap" },
    { feature: "Acordes y estructura", free: "Basico", pro: "Avanzado beta", studio: "Roadmap" },
    { feature: "Loop por seccion", free: "Basico", pro: "Avanzado beta", studio: "Roadmap" },
    { feature: "Separacion de stems", free: "Disponible segun flujo", pro: "Practice avanzado", studio: "Roadmap" },
    { feature: "Stem Mixer presets", free: "Lectura/mezcla", pro: "Guardar/editar", studio: "Roadmap" },
    { feature: "Export PDF", free: "Basico segun dominio", pro: "Letra + acordes", studio: "Roadmap" },
    { feature: "Export MIDI", free: "Coming soon", pro: "Coming soon", studio: "Roadmap" },
    { feature: "Export audio renderizado", free: false, pro: "Coming soon", studio: "Roadmap" },
    { feature: "Asistente IA", free: "Limite diario", pro: "Sin limite Free", studio: "Roadmap" },
    { feature: "Checkout/billing", free: false, pro: "Coming soon", studio: "Coming soon" },
  ];

  return (
    <div className="min-h-screen bg-[#09090b]">
      <nav className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <AudioWaveform className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-white font-['Manrope']">SteelMora</span>
          </Link>

          <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </nav>

      <section className="pt-16 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">
            <Sparkles className="w-3 h-3 mr-1" />
            MVP Early Access
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Manrope'] mb-4" data-testid="pricing-title">
            Planes y precios
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Esta pantalla describe el MVP actual y el roadmap Pro. El upgrade/billing real todavia no esta activo.
          </p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.name}
                className={`relative bg-[#18181b] border-white/10 ${
                  plan.highlighted
                    ? "border-primary/50 shadow-[0_0_30px_rgba(139,92,246,0.2)]"
                    : ""
                }`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-white px-4 py-1">{plan.badge}</Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                    plan.highlighted ? "bg-primary/20 text-primary" : "bg-white/5 text-gray-400"
                  }`}>
                    {plan.icon}
                  </div>
                  <CardTitle className="text-2xl font-bold text-white font-['Manrope']">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-gray-400">{plan.description}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      {plan.price === "0" && <span className="text-lg text-gray-400">$</span>}
                      <span className="text-5xl font-bold text-white font-['JetBrains_Mono']">{plan.price}</span>
                    </div>
                    <span className="text-sm text-gray-500">{plan.period}</span>
                  </div>

                  <div className="space-y-3">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-secondary" />
                        </div>
                        <span className="text-sm text-gray-300">{feature}</span>
                      </div>
                    ))}

                    {plan.limitations.map((limitation) => (
                      <div key={limitation} className="flex items-start gap-3 opacity-70">
                        <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs text-amber-400">!</span>
                        </div>
                        <span className="text-sm text-gray-500">{limitation}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleSelectPlan(plan.name)}
                    className={`w-full h-12 ${
                      plan.highlighted
                        ? "bg-primary hover:bg-primary/90 btn-glow"
                        : plan.name === "Free"
                          ? "bg-white/10 text-gray-300 hover:bg-white/20"
                          : "bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10"
                    }`}
                    disabled={plan.name === "Studio"}
                    data-testid={`select-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-[#18181b] border-white/10 overflow-hidden">
            <CardHeader>
              <CardTitle className="text-xl font-['Manrope'] text-white">
                Comparacion honesta del MVP
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Caracteristica</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-gray-400">Free</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-primary">Pro MVP</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-gray-400">Studio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row) => (
                      <tr key={row.feature} className="border-b border-white/5">
                        <td className="py-4 px-6 text-sm text-gray-300">{row.feature}</td>
                        {[row.free, row.pro, row.studio].map((value, index) => (
                          <td key={`${row.feature}-${index}`} className={`py-4 px-4 text-center ${index === 1 ? "bg-primary/5" : ""}`}>
                            {typeof value === "boolean" ? (
                              value ? (
                                <Check className="w-5 h-5 text-secondary mx-auto" />
                              ) : (
                                <span className="text-gray-600">-</span>
                              )
                            ) : (
                              <span className={`text-sm ${index === 1 ? "text-primary" : "text-gray-400"}`}>{value}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white font-['Manrope'] mb-4">
            MVP en evolucion
          </h2>
          <p className="text-gray-400 mb-8">
            Si quieres acceso Pro durante la beta, solicita acceso al equipo. El pago automatico se integrara en una fase posterior.
          </p>
          <Link to="/auth?mode=register">
            <Button className="bg-primary hover:bg-primary/90 btn-glow gap-2">
              Empezar gratis ahora
              <Sparkles className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          © 2026 SteelMora. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;

